import { yup } from 'common/validation/initYup';
import { ValidationSchema } from 'common/validation/ValidationSchema';
import { Type, Transform, plainToClass, classToPlain } from 'class-transformer';
import { TypeTransformDecimal } from 'common/dto/shared/types/TypeTransformDecimal';
import { Decimal } from 'decimal.js-light';
import { ApiProperty } from '@nestjs/swagger';
import type { IDto, IDtoPartial } from 'common/dto/shared/DtoInterfaces';
import { PaginatedMetaDto } from 'common/dto/shared/PaginatedDto';
import { Roles } from 'common/permissions/Roles';
import { ValidationAction } from 'common/validation/ValidationAction';
import { UserAuthoritiesSelectDto } from '../documental-bridge/UserAuthoritiesSelectDto';
/**
 * Rappresentazione DTO della classe User definita in: src/server/schema/user/User.entity.ts
 * Hash: ae4ab065fd355b44cbdd3872dec34292
 */
@ValidationSchema(() => UserSchema)
export class UserDto {
  @ApiProperty({ required: false, type: Number })
  id!: number;
  @ApiProperty({ type: String, description: 'Username' })
  login!: string;
  @ApiProperty({ type: String, description: 'Nome Utente' })
  name!: string;
  @ApiProperty({ required: false, type: String, description: 'Codice univoco documentale' })
  serial?: string | null | undefined;
  @ApiProperty({ description: 'Ruolo' })
  role!: "SuperAdmin" | "DocumentalUser" | "GenericUser" | "Compiler" | "QualitySecretary" | "QualityManager" | "RiskManager" | "Approver" | "StrategicDirector";
  @ApiProperty({ required: false, type: String, description: 'Email' })
  email?: string | null | undefined;
  @ApiProperty({ required: false, type: String, format: 'date-time' })
  createdAt!: Date;
  @ApiProperty({ required: false, type: String, format: 'date-time' })
  updatedAt!: Date;
  @ApiProperty({ required: false, type: String, format: 'date-time' })
  deletedAt?: Date | undefined;
  @ApiProperty({ required: false, type: String })
  serviceToken?: string | null | undefined;
  @ApiProperty({ required: false, type: String, description: 'Codice utente documentale' })
  documentalUserCode?: string | null | undefined;
  @ApiProperty({ type: Boolean, description: 'Autorizzazione attraverso password' })
  enablePassword!: boolean;
  @ApiProperty({ required: false, type: String, description: 'Password' })
  password?: string;
  @ApiProperty({ required: false, type: [UserAuthoritiesSelectDto] })
  avaiableRoles!: UserAuthoritiesSelectDto[];
  @ApiProperty({ required: false })
  selectedRole?: "SuperAdmin" | "DocumentalUser" | "GenericUser" | "Compiler" | "QualitySecretary" | "QualityManager" | "RiskManager" | "Approver" | "StrategicDirector" | undefined;
  @ApiProperty({ required: false, type: Number })
  foliumOfficeId?: number | undefined;
  @ApiProperty({ required: false, type: String })
  foliumJwt?: string | undefined;
  @ApiProperty({ required: false, type: String })
  foliumRefreshJwt?: string | undefined;
  @ApiProperty({ type: String })
  strategy!: string;
  @ApiProperty({ type: String })
  originalStrategy!: string;

  /** Proprietà che identifica i DTO */
  readonly __dto!: any;

  /**
   * Crea una nuova istanza con i valori forniti
   */
  constructor(values?: IDtoPartial<UserDto>) {
    if (values != null) {
      Object.assign(this, values instanceof UserDto ? values : plainToClass(UserDto, values));
    }
  }

  getRole() {
    return Roles[this.selectedRole ?? this.role];
  }

  async validate(options?: any) {
    const validated = await UserSchema.validate(classToPlain(this), options);
    return new UserDto(validated);
  }
}

/** Interfaccia simmetrica al DTO UserDto */
export type IUserType = IDto<UserDto>;

/**
 * DTO Paginato della classe User
 */
export class PaginatedUserDto {
  @ApiProperty({ type: [UserDto] })
  @Type(() => UserDto)
  items!: UserDto[];
  @ApiProperty({ type: PaginatedMetaDto })
  meta!: PaginatedMetaDto;
}

export const UserSchema = yup
  .object({
    id: yup.number(),
    login: yup.string().required().label('Username'),
    name: yup.string().required().label('Nome Utente'),
    serial: yup.string().nullable().label('Codice univoco documentale'),
    role: yup.mixed().required().label('Ruolo'),
    email: yup.string().nullable().label('Email'),
    serviceToken: yup.string().nullable(),
    documentalUserCode: yup.string().nullable().label('Codice utente documentale'),
    enablePassword: yup.boolean().required().label('Autorizzazione attraverso password'),
    password: yup.string().when(['$action', 'enablePassword'], {
          is: (action: string, enablePassword: boolean) =>
            action === ValidationAction.create && enablePassword,
          then: (schema: any) => schema.required()
        }).label('Password')
  })
  .noUnknown()
  .meta({ schemaName: "UserSchema" })
  .required();
